import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";

import {
	SECTION_SPEC_LIFECYCLE,
	type SectionSpecDetail,
	type SectionSpecVersion,
	type SpecItemPayload,
} from "@/shared/models/SectionSpec";
import { formatSectionSpecRevision } from "@/shared/utils/sectionSpecRevision";
import {
	formatSectionSpecDimensionValue,
	parseOptionalSectionSpecDecimal,
	type SectionSpecDraftFormValues,
	type SpecItemDraftRow,
} from "./SectionSpecForm";

import { isAngleSpecItem } from "@/shared/utils/sectionSpecUnits";

const moment = getMomentTz();

const formatNumber = (value: number | null | undefined) =>
	value == null ? "" : String(value);

export const parseSectionSpecNumber = (raw: string): number | null => {
	const trimmed = raw.trim();
	if (trimmed === "") return null;
	const value = Number(trimmed);
	return Number.isFinite(value) ? value : null;
};

export const formatEffectiveDate = (value: string | null | undefined) => {
	if (!value) return "-";
	const date = moment.utc(value);
	return date.isValid()
		? dxtToLocalServerTime(date.toDate(), "MM/dd/yyyy HH:mm")
		: "Invalid date";
};

export const formatVersionLabel = (version: SectionSpecVersion) => {
	if (version.LifecycleCode === SECTION_SPEC_LIFECYCLE.DRAFT) return "Draft";

	const revision = formatSectionSpecRevision(version.Rev, version.SubRev);
	if (version.LifecycleCode === SECTION_SPEC_LIFECYCLE.PUBLISHED)
		return revision;
	const lifecycle =
		version.LifecycleCode === SECTION_SPEC_LIFECYCLE.CURRENT
			? "Current"
			: version.LifecycleLabel;
	return `${revision} - ${lifecycle}`;
};

const lifecycleOrder = {
	[SECTION_SPEC_LIFECYCLE.DRAFT]: 0,
	[SECTION_SPEC_LIFECYCLE.SCHEDULED]: 1,
	[SECTION_SPEC_LIFECYCLE.CURRENT]: 2,
	[SECTION_SPEC_LIFECYCLE.PUBLISHED]: 3,
} as const;

export const compareVersions = (
	left: SectionSpecVersion,
	right: SectionSpecVersion,
) => {
	const lifecycleDifference =
		lifecycleOrder[left.LifecycleCode] - lifecycleOrder[right.LifecycleCode];
	if (lifecycleDifference !== 0) return lifecycleDifference;

	const leftDate = moment.utc(left.EffectiveDate);
	const rightDate = moment.utc(right.EffectiveDate);
	if (leftDate.isValid() && rightDate.isValid()) {
		const dateDifference = leftDate.diff(rightDate);
		if (dateDifference !== 0)
			return left.LifecycleCode === SECTION_SPEC_LIFECYCLE.SCHEDULED
				? dateDifference
				: -dateDifference;
	}
	return (right.Rev ?? -1) - (left.Rev ?? -1);
};

interface SectionSpecFormOptions {
	create?: boolean;
}

export const toSectionSpecFormValues = (
	detail: SectionSpecDetail,
	options: SectionSpecFormOptions = {},
): SectionSpecDraftFormValues => {
	const spec = detail.spec;
	const formatBand = (
		value: number | null,
		item: Pick<SpecItemDraftRow, "Code" | "IsDegrees">,
	) => formatSectionSpecDimensionValue(formatNumber(value), item);
	return {
		printNo: spec?.PrintNo ?? "",
		area: formatNumber(spec?.Area),
		target: formatNumber(spec?.TargetLbsPerFoot),
		min: formatNumber(spec?.MinLbsPerFoot),
		max: formatNumber(spec?.MaxLbsPerFoot),
		legDelta: formatNumber(spec?.LegDeltaMax),
		items: detail.items.map((item) => ({
			ProfileItemId: item.ProfileItemId,
			ProfileCategoryId: item.ProfileCategoryId,
			CategoryName: item.CategoryName,
			CategorySortOrder: item.CategorySortOrder,
			Code: item.Code,
			Name: item.Name,
			DisplayName: item.DisplayName,
			ValueTypeCode: item.ValueTypeCode,
			SortOrder: item.SortOrder,
			IsActive: item.IsActive,
			IsDegrees: isAngleSpecItem(item),
			Applies:
				options.create && spec?.ProfileSpecId == null ? true : item.Applies,
			Ltl: formatBand(item.Ltl, item),
			Nominal: formatBand(item.Nominal, item),
			Utl: formatBand(item.Utl, item),
		})),
	};
};

export const toItemPayload = (rows: SpecItemDraftRow[]): SpecItemPayload[] =>
	rows
		.filter((row) => row.Applies)
		.map((row) => ({
			ProfileItemId: row.ProfileItemId,
			IsDegrees: row.IsDegrees,
			Ltl: parseOptionalSectionSpecDecimal(row.Ltl),
			Nominal: parseOptionalSectionSpecDecimal(row.Nominal),
			Utl: parseOptionalSectionSpecDecimal(row.Utl),
		}));
